import type { AssessmentType, FreeResult } from '../apiClient';

/**
 * The dashboard Overview's derivations, copied from production.
 *
 * Pure functions, so the ordering rules production encodes as a list of `if`
 * branches can be asserted without mounting anything.
 *
 * **One ordering difference, and it is the only one.** Production's
 * `/assessments/me` returns oldest first and it reads the latest as
 * `list[list.length - 1]`; V2's returns newest first, so the latest is
 * `list[0]` and the previous is `list[1]`. Same two assessments, reached from
 * the other end.
 */
export const REASSESSMENT_WINDOW_DAYS = 30;

/** Every assessment of one type, newest first, as the endpoint returns them. */
export function ofType(assessments: FreeResult[], type: AssessmentType): FreeResult[] {
  return assessments.filter((a) => a.assessmentType === type);
}

export function latestOf(assessments: FreeResult[], type: AssessmentType): FreeResult | null {
  return ofType(assessments, type)[0] ?? null;
}

/** The one before the latest — production's retake comparison. */
export function previousOf(assessments: FreeResult[], type: AssessmentType): FreeResult | null {
  return ofType(assessments, type)[1] ?? null;
}

/**
 * Production offers a reassessment thirty days after the latest Bottleneck.
 * The comparison is strict, as production's is.
 */
export function reassessmentAvailable(latestBottleneck: FreeResult | null, now: Date): boolean {
  if (!latestBottleneck?.completedAt) return false;
  const completed = new Date(latestBottleneck.completedAt).getTime();
  if (!Number.isFinite(completed)) return false;
  return completed + REASSESSMENT_WINDOW_DAYS * 24 * 60 * 60 * 1000 < now.getTime();
}

/**
 * One entry in production's **Next Best Action** list.
 *
 * The label is production's sentence. `action` names which control to render;
 * the component owns the markup, because production's CTA is JSX inline in the
 * same list and V2 cannot put JSX in a pure module.
 */
export interface NextAction {
  label: string;
  action:
    | 'take-bottleneck'
    | 'take-reassessment'
    | 'open-report'
    | 'view-work-fit-result'
    | 'explore-work-fit'
    | 'open-work-fit-report';
  /** The result the control addresses, where it addresses one. */
  resultId?: string;
}

/**
 * Production's ordered priority list, branch for branch.
 *
 * The empty branch is production's too: a customer who has taken the Bottleneck
 * assessment but not unlocked it gets **no** entry here, because the unlock is
 * offered further down the page. Reproducing that means reproducing the gap.
 */
export function nextActions(assessments: FreeResult[], now: Date): NextAction[] {
  const latestBottleneck = latestOf(assessments, 'bottleneck');
  const latestWorkFit = latestOf(assessments, 'work_fit');
  const bottleneckPaid = latestBottleneck?.isUnlocked === true;
  const workFitPaid = latestWorkFit?.isUnlocked === true;

  const actions: NextAction[] = [];

  if (!latestBottleneck) {
    actions.push({
      label: 'Take the Bottleneck Assessment to identify your primary performance pattern.',
      action: 'take-bottleneck',
    });
  } else if (!bottleneckPaid) {
    // Production's own empty branch: the unlock options live further down.
  } else if (reassessmentAvailable(latestBottleneck, now)) {
    actions.push({
      label:
        'It has been 30 days since your last assessment. Reassess to track your progress.',
      action: 'take-reassessment',
    });
  } else if (bottleneckPaid) {
    actions.push({
      label: 'Work through your 7-Day Reset Journal and 30-Day Workbook.',
      action: 'open-report',
      resultId: latestBottleneck.id,
    });
    if (latestWorkFit && !workFitPaid) {
      actions.push({
        label:
          'Review your Work Fit result before your next role, manager, or career conversation.',
        action: 'view-work-fit-result',
        resultId: latestWorkFit.id,
      });
    }
  }

  // Suggest Work Fit only when not yet completed.
  if (latestBottleneck && !latestWorkFit) {
    actions.push({
      label:
        'Explore the Work Fit Assessment to understand the environments where your performance style thrives.',
      action: 'explore-work-fit',
    });
  }

  // Fallback: a paying customer with no other action is pointed at their report.
  if (actions.length === 0 && (bottleneckPaid || workFitPaid)) {
    if (bottleneckPaid && latestBottleneck) {
      actions.push({
        label: 'Work through your full Bottleneck report and 30-day reset plan.',
        action: 'open-report',
        resultId: latestBottleneck.id,
      });
    } else if (workFitPaid && latestWorkFit) {
      actions.push({
        label:
          'Review your Work Fit report and the environments where your performance style thrives.',
        action: 'open-work-fit-report',
        resultId: latestWorkFit.id,
      });
    }
  }

  return actions;
}
